interface SystemData {
  id: string;
  label: string;
  tagline: string;
  icon: string;
  accentColor: string;
  accentBg: string;
  borderColor: string;
  image: string;
  imageAlt: string;
  description: string;
  pros: string[];
  cons: string[];
  bestFor: string[];
  notIdealFor: string[];
  shopUrl: string;
  shopLabel: string;
  stat1Value: string;
  stat1Label: string;
  stat2Value: string;
  stat2Label: string;
  stat3Value: string;
  stat3Label: string;
}

const systems: SystemData[] = [
  {
    id: "solar",
    label: "Solar Generator",
    tagline: "Clean. Silent. Infinitely Renewable.",
    icon: "ri-sun-line",
    accentColor: "text-amber-600",
    accentBg: "bg-amber-50",
    borderColor: "border-amber-200",
    image: "https://public.readdy.ai/ai/img_res/513a8c10d0d26a7ee4506944a37eed20.jpg",
    imageAlt: "Solar generator system for home backup power",
    description: "Solar generators combine a high-capacity lithium battery with solar charging capability — giving you clean, silent power indoors or outdoors with no fuel, no fumes, and no engine maintenance. When paired with solar panels, they can charge indefinitely as long as the sun is shining, making them the ideal choice for extended outages, off-grid living, and day-to-day energy independence.",
    pros: [
      "Zero fuel cost — sun is free",
      "Completely silent operation",
      "Safe for indoor use — no fumes",
      "Portable and versatile",
      "App-connected real-time monitoring",
      "Expandable with additional battery modules",
      "Low maintenance — no oil changes, no spark plugs",
      "Works during any outage regardless of gas availability",
    ],
    cons: [
      "Higher upfront cost than basic gas generators",
      "Charging limited by sunlight in overcast weather",
      "May not power heavy HVAC without a high-end model",
      "Battery degrades over 8–12 years",
    ],
    bestFor: [
      "Homeowners wanting zero-maintenance backup",
      "Families with medical devices requiring clean power",
      "Off-grid and remote cabin applications",
      "Anyone who wants portable + home power in one unit",
      "Preparedness-minded households",
    ],
    notIdealFor: [
      "Powering a 200+ amp whole-home load for weeks",
      "Those unwilling to invest upfront",
    ],
    shopUrl: "https://ironhavensupply.com/collections/solar-generators",
    shopLabel: "Shop Solar Generators",
    stat1Value: "Silent",
    stat1Label: "Noise Level",
    stat2Value: "$0/yr",
    stat2Label: "Fuel Cost",
    stat3Value: "3,000+",
    stat3Label: "Charge Cycles",
  },
  {
    id: "gas",
    label: "Gas Generator",
    tagline: "High Power. Simple. Time-Tested.",
    icon: "ri-oil-line",
    accentColor: "text-slate-600",
    accentBg: "bg-slate-50",
    borderColor: "border-slate-200",
    image: "https://readdy.ai/api/search-image?query=portable%20gas%20powered%20generator%20outdoors%20on%20concrete%20pad%20during%20power%20outage%20running%20with%20cords%20attached%20residential%20home%20in%20background%20overcast%20sky%20dramatic%20emergency%20lighting%20realistic%20editorial%20photography%20neutral%20gray%20tones&width=700&height=460&seq=gas-generator-deepdive-ihs&orientation=landscape",
    imageAlt: "Gas generator for home backup power",
    description: "Gas generators are the traditional workhorse of home backup power — affordable upfront, widely available, and capable of running heavy loads when fuel is on hand. They're reliable for short-duration outages and situations where a large power draw is needed quickly. However, they require ventilation, fuel storage, and regular maintenance to stay reliable when you need them most.",
    pros: [
      "Lowest upfront cost of any option",
      "High wattage output for the price",
      "Widely available at hardware stores",
      "Familiar technology — easy to source parts",
      "Can run heavy loads (AC units, sump pumps)",
      "No battery degradation over time",
    ],
    cons: [
      "Produces CO — must be used outdoors only",
      "Loud — typically 65–75 dB during operation",
      "Requires gasoline which can be scarce during disasters",
      "Regular maintenance: oil changes, spark plugs, carb",
      "Fuel degrades in storage — stabilizer required",
      "No solar charging or battery backup integration",
      "Cannot start automatically during an outage",
    ],
    bestFor: [
      "Short outages where fuel is readily available",
      "Budget-constrained buyers needing high wattage",
      "Contractors and job sites",
      "Rural properties with gasoline storage capability",
    ],
    notIdealFor: [
      "Extended disaster scenarios where gas may be unavailable",
      "Indoor or apartment use (CO risk)",
      "Anyone wanting a set-it-and-forget-it solution",
    ],
    shopUrl: "https://ironhavensupply.com/collections/home-backup-power",
    shopLabel: "Shop Backup Power",
    stat1Value: "65–75 dB",
    stat1Label: "Noise Level",
    stat2Value: "$3–6/hr",
    stat2Label: "Fuel Cost",
    stat3Value: "Outdoors",
    stat3Label: "Use Location",
  },
  {
    id: "whole-home",
    label: "Whole Home Standby",
    tagline: "Seamless. Automatic. Always Ready.",
    icon: "ri-building-2-line",
    accentColor: "text-slate-700",
    accentBg: "bg-slate-50",
    borderColor: "border-slate-200",
    image: "https://readdy.ai/api/search-image?query=large%20standby%20home%20generator%20installed%20next%20to%20house%20exterior%20professional%20installation%20gray%20metal%20enclosure%20permanent%20fixture%20suburban%20home%20lush%20green%20lawn%20natural%20gas%20line%20connected%20professional%20installation%20photography%20clean%20neutral%20background&width=700&height=460&seq=whole-home-standby-deepdive-ihs&orientation=landscape",
    imageAlt: "Whole home standby generator permanently installed",
    description: "Whole-home standby generators are permanently installed, connected to your natural gas or propane line, and wired directly into your home's electrical panel. They activate automatically within seconds of an outage — your lights barely flicker. If budget isn't a constraint and you need 100% seamless coverage for your entire home, this is the gold standard. The catch: installation costs can rival a new HVAC system.",
    pros: [
      "100% automatic — zero action required from homeowner",
      "Powers entire home: HVAC, stove, water heater, everything",
      "Connected to gas line — no manual fueling",
      "Runs self-tests weekly to ensure readiness",
      "Very quiet for the output level",
      "Adds resale value to your property",
    ],
    cons: [
      "Highest upfront cost: $5,000–$20,000+",
      "Installation requires licensed electrician + permit",
      "Not portable — permanently attached to property",
      "Annual professional maintenance required",
      "Requires natural gas or large propane tank infrastructure",
      "Long lead times for installation (weeks to months)",
    ],
    bestFor: [
      "Homeowners who want zero hands-on involvement",
      "Properties with frequent or extended grid outages",
      "High-dependency medical equipment households",
      "Luxury homes or properties where downtime is unacceptable",
    ],
    notIdealFor: [
      "Renters or apartment dwellers",
      "Budget-conscious buyers",
      "Anyone wanting portability",
    ],
    shopUrl: "https://ironhavensupply.com/collections/home-backup-power",
    shopLabel: "Shop Home Backup Systems",
    stat1Value: "Automatic",
    stat1Label: "Activation",
    stat2Value: "Whole Home",
    stat2Label: "Coverage",
    stat3Value: "$5k–$20k+",
    stat3Label: "Total Install Cost",
  },
];

function SystemCard({ system }: { system: SystemData }) {
  return (
    <section id={system.id} className="py-20 scroll-mt-20 even:bg-gray-50 odd:bg-white">
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-start">
          {/* Image + stats */}
          <div>
            <div className="relative rounded-2xl overflow-hidden">
              <img
                src={system.image}
                alt={system.imageAlt}
                className="w-full h-72 md:h-96 object-cover object-top"
              />
              {/* Stats overlay */}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-gray-950/90 to-transparent px-5 py-5">
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { v: system.stat1Value, l: system.stat1Label },
                    { v: system.stat2Value, l: system.stat2Label },
                    { v: system.stat3Value, l: system.stat3Label },
                  ].map((s) => (
                    <div key={s.l} className="text-center">
                      <p className="text-amber-400 font-extrabold text-base leading-tight">{s.v}</p>
                      <p className="text-gray-400 text-xs mt-0.5">{s.l}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* CTA block */}
            <div className={`mt-5 rounded-xl border ${system.borderColor} ${system.accentBg} p-5`}>
              <p className="font-bold text-gray-800 text-sm mb-1">Ready to shop {system.label}s?</p>
              <p className="text-gray-500 text-xs mb-4">Browse our full selection of authorized {system.label} models — all with manufacturer warranties.</p>
              <a
                href={system.shopUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-white text-sm font-bold px-5 py-2.5 rounded-lg transition-colors whitespace-nowrap cursor-pointer"
              >
                {system.shopLabel}
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line text-sm"></i>
                </div>
              </a>
            </div>
          </div>

          {/* Content */}
          <div>
            <div className={`inline-flex items-center gap-2 mb-4 px-3 py-1.5 rounded-full ${system.accentBg} border ${system.borderColor}`}>
              <div className="w-4 h-4 flex items-center justify-center">
                <i className={`${system.icon} ${system.accentColor} text-sm`}></i>
              </div>
              <span className={`${system.accentColor} text-xs font-bold uppercase tracking-wider`}>{system.label}</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-2">{system.label}</h2>
            <p className="text-amber-600 font-semibold text-base mb-5">{system.tagline}</p>
            <p className="text-gray-600 text-sm md:text-base leading-relaxed mb-8">{system.description}</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {/* Pros */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-5 h-5 flex items-center justify-center bg-emerald-100 rounded-full">
                    <i className="ri-thumb-up-line text-emerald-600 text-xs"></i>
                  </div>
                  <span className="font-bold text-gray-800 text-sm uppercase tracking-wide">Advantages</span>
                </div>
                <ul className="space-y-2">
                  {system.pros.map((p) => (
                    <li key={p} className="flex items-start gap-2 text-sm text-gray-600">
                      <div className="w-4 h-4 flex items-center justify-center mt-0.5 shrink-0">
                        <i className="ri-checkbox-circle-fill text-emerald-500 text-sm"></i>
                      </div>
                      {p}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Cons */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-5 h-5 flex items-center justify-center bg-red-50 rounded-full">
                    <i className="ri-thumb-down-line text-red-400 text-xs"></i>
                  </div>
                  <span className="font-bold text-gray-800 text-sm uppercase tracking-wide">Limitations</span>
                </div>
                <ul className="space-y-2">
                  {system.cons.map((c) => (
                    <li key={c} className="flex items-start gap-2 text-sm text-gray-600">
                      <div className="w-4 h-4 flex items-center justify-center mt-0.5 shrink-0">
                        <i className="ri-close-circle-line text-red-400 text-sm"></i>
                      </div>
                      {c}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Best for / Not ideal for */}
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-4">
                <p className="font-bold text-emerald-800 text-xs uppercase tracking-wide mb-2">Best For</p>
                <ul className="space-y-1">
                  {system.bestFor.map((b) => (
                    <li key={b} className="text-xs text-emerald-700 flex items-start gap-1.5">
                      <i className="ri-arrow-right-s-line text-emerald-500 mt-0.5 shrink-0"></i>
                      {b}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-red-50 border border-red-100 rounded-xl p-4">
                <p className="font-bold text-red-700 text-xs uppercase tracking-wide mb-2">Not Ideal For</p>
                <ul className="space-y-1">
                  {system.notIdealFor.map((n) => (
                    <li key={n} className="text-xs text-red-600 flex items-start gap-1.5">
                      <i className="ri-arrow-right-s-line text-red-400 mt-0.5 shrink-0"></i>
                      {n}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function SystemDeepDives() {
  return (
    <>
      {/* Section header */}
      <div className="py-14 bg-gray-900 text-center">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <span className="text-amber-400 text-xs font-bold uppercase tracking-widest">Deep Dive Analysis</span>
          <h2 className="text-3xl md:text-4xl font-extrabold text-white mt-2 mb-3">
            The Real Story Behind Each System
          </h2>
          <p className="text-gray-400 text-base max-w-xl mx-auto">
            The specs only tell part of the story. Here&apos;s what it&apos;s actually like to own and live with each type.
          </p>
        </div>
      </div>
      {systems.map((s) => (
        <SystemCard key={s.id} system={s} />
      ))}
    </>
  );
}
